import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { Box, Text, useDisclosure, useToast } from "@chakra-ui/react";
import { useQueryClient } from "react-query";
import { FormTextArea, NumericFormInput } from "components/Forms";
import CategoryForm from "./CategoryForm";
import {
  organizationQuestionnaireKeys,
  purchasedGoodsAndServicesKeys,
  useAddPurchasedProductItems,
  useAttachFileToCategory, useDeleteCategoryAttachment,
  useDeletePurchasedProductItem, useGetCategoryAttachment,
  useGetLocations,
  useGetPurchasedProductItems,
  useUpdatePurchasedProductItem
} from "../../../../../../hooks/query";
import { useUpdatePurchasedGoodsAndServices } from "../../../../../../hooks/query/scope-3";
import { nonNumericSymbols, showErrorToast, showSuccessToast } from "../../../../../../utils";
import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { CategoryGrid } from "../CategoryGrid";
import { DeleteDialog } from "../../../../../DataEntryPlanning/components/DeleteDialog";
import { hideAppSpinner, showAppSpinner } from "../../../../../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import { PurchasedAndCapitalGoodsModalWrapper } from "../CategoryModalWrappers";
import { ManualInput } from "../ManualInput";
import { FileAttachment } from "../../../FileAttachment";

const FORM_INPUT_KEY = "purchasedGoods";
const CUBIC_METERS = 4;

function PurchasedGoodsAndServices({
                                     defaultValues,
                                     tabKey,
                                     handleCategoryChange,
                                     finalize,
                                     finalizeQuestionnaire,
                                     isPreview
                                   }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { t: globalTranslation } = useTranslation("translation");
  const { t } = useTranslation("translation", {
    keyPrefix: "organizationQuestionnaire.scope3"
  });
  const dispatch = useDispatch();

  const {
    data: locations
  } = useGetLocations({
    onError: err => handleErrors(err)
  });

  const {
    id: questionnaireId,
    categories: {
      purchasedGoodsAndServices: {
        id: categoryId,
        waterUsage,
        notes,
        manualInputOfEmissions,
        totalCo2Emissions,
        attachment
      } = {
        id: null,
        waterUsage: null,
        notes: "",
        manualInputOfEmissions: false,
        totalCo2Emissions: null,
        attachment: null
      }
    }
  } = defaultValues;

  const colHeaders = [
    t(`inputs.${tabKey}.supplierName`),
    t(`inputs.${tabKey}.location`),
    t(`inputs.${tabKey}.productCode`),
    t(`inputs.${tabKey}.itemDescription`),
    "Amount spent"
  ];
  const attributesOrder = ["supplierName", "location", "productCategory", "itemDescription", "spentMoneyAmount"];

  const [categoryModalData, setCategoryModalData] = useState(null);

  const {
    isOpen: isCategoryModalOpen,
    onOpen: onCategoryModalOpen,
    onClose: onCategoryModalClose
  } = useDisclosure();

  const {
    isOpen: isDeleteItemDialogOpen,
    onOpen: onDeleteItemDialogOpen,
    onClose: onDeleteItemDialogClose
  } = useDisclosure();

  const handleErrors = (err) => {
    if (err.response?.status === 404) {
      return;
    }
    showErrorToast(toast, err.message);
  };

  const purchasedGoodsDefaultValues = {
    notes,
    waterUsage: waterUsage?.value != null ? waterUsage?.value.toString() : ""
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    reset,
    control,
    setValue
  } = useForm({
    defaultValues: purchasedGoodsDefaultValues
  });

  useEffect(() => {
    reset({
      notes, manualInputOfEmissions, totalCo2Emissions,
      waterUsage: waterUsage?.value != null ? waterUsage?.value.toString() : ""
    });
  }, [defaultValues, reset, notes, waterUsage, manualInputOfEmissions, totalCo2Emissions]);

  const waterUsageValue = watch("waterUsage");

  const prepareSectionValues = useCallback(items => {
    return items.map(item => {
      return {
        id: item.id ?? null,
        location: locations?.find(el => el.id === item.locationId)?.location ?? "",
        locationId: item.locationId,
        productCategory: `${item.productCode} - ${item.productCategory}`,
        productCategoryId: item.productCategoryId ?? null,
        supplierName: item.supplierName,
        itemDescription: item.itemDescription,
        spentMoneyAmount: item.spentMoneyAmount
      };
    });
  }, [locations]);

  const handleAddItem = () => {
    setCategoryModalData({
      fieldArrayName: FORM_INPUT_KEY,
      columnHeaders: [...colHeaders, ""],
      modalHeaderItemsType: t(`inputs.purchasedGoodsAndServices.itemsType`)
    });
    onCategoryModalOpen();
  };

  const handleEditItem = (item) => {
    setCategoryModalData({
      fieldArrayName: FORM_INPUT_KEY,
      editData: {
        id: item.id,
        productCategory: item.productCategory,
        productCategoryId: item.productCategoryId,
        supplierName: item.supplierName,
        itemDescription: item.itemDescription,
        spentMoneyAmount: item.spentMoneyAmount,
        locationId: item.locationId,
        location: item.location
      },
      columnHeaders: colHeaders,
      modalHeaderItemsType: t(`inputs.purchasedGoodsAndServices.itemsType`)
    });
    onCategoryModalOpen();
  };

  const [selectedItem, setSelectedItem] = useState(null);

  const handleDeleteItem = (item) => {
    onDeleteItemDialogOpen();
    setSelectedItem({ id: item.id });
  };

  const handleDeleteItemDialogClose = () => {
    setSelectedItem(null);
    onDeleteItemDialogClose();
  };

  const deleteRecord = () => {
    dispatch(showAppSpinner());
    deleteItem({ questionnaireId, recordId: selectedItem.id });
  };

  const queryConfig = {
    onSuccess: () => {
      showSuccessToast(toast, "Category Purchased Goods and Services saved!");
      if (finalize) {
        finalizeQuestionnaire(questionnaireId);
      } else {
        queryClient.invalidateQueries(organizationQuestionnaireKeys.orgQuestionnaireById());
        if (!isLoadingOnUpdate) {
          handleCategoryChange();
        }
      }
    },
    onError: err => showErrorToast(toast, err.message)
  };

  const formId = `formStep${tabKey}`;
  const { mutate: update, isLoading: isLoadingOnUpdate } = useUpdatePurchasedGoodsAndServices(queryConfig);

  const { mutate: deleteItem } = useDeletePurchasedProductItem({
    onSuccess: () => {
      handleDeleteItemDialogClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.deleted"));
      queryClient.invalidateQueries(purchasedGoodsAndServicesKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const onSubmit = ((data) => {
    if (!isPreview) {
      if (isDirty) {
        const requestData = {
          waterUsage: {
            value: data.waterUsage !== "" ? Number(data.waterUsage) : null,
            measureId: CUBIC_METERS
          },
          notes: data.notes,
          totalCo2Emissions: data.totalCo2Emissions,
          manualInputOfEmissions: data.manualInputOfEmissions
        };
        update({ questionnaireId, data: requestData });
      } else {
        finalize ? finalizeQuestionnaire(questionnaireId) : handleCategoryChange();
      }
    } else {
      handleCategoryChange();
    }
  });

  const handleCategoryModalClose = () => {
    setCategoryModalData(null);
    onCategoryModalClose();
  };

  const { mutate: addItem } = useAddPurchasedProductItems({
    onSuccess: () => {
      handleCategoryModalClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.added"));
      queryClient.invalidateQueries(purchasedGoodsAndServicesKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: updateItem } = useUpdatePurchasedProductItem({
    onSuccess: () => {
      handleCategoryModalClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.updated"));
      queryClient.invalidateQueries(purchasedGoodsAndServicesKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const handleCategoryModalSubmit = (formData) => {
    const apiData = [];
    formData[categoryModalData.fieldArrayName].forEach(el => apiData.push(el));

    dispatch(showAppSpinner());
    if (categoryModalData.editData) {
      updateItem({
        questionnaireId,
        recordId: apiData[0].id,
        data: apiData[0]
      });
    } else {
      addItem({
        questionnaireId,
        data: apiData
      });
    }
  };

  return (
    <CategoryForm onSubmit={handleSubmit(onSubmit)} id={formId} marginTop={4}>
      <Text opacity="0.75" fontSize="sm" marginBottom={4}>
        {t(`categories.${tabKey}Desc`)}
      </Text>

      <Box marginBottom="4">
        <NumericFormInput
          label={t(`inputs.waterUsage`)}
          errors={errors}
          {...register("waterUsage")}
          onKeyDown={(e) => nonNumericSymbols.includes(e.key) && e.preventDefault()}
          title={waterUsageValue}
          disabled={isPreview}
        />
      </Box>

      {locations && <CategoryGrid
        questionnaireId={questionnaireId}
        columnHeaders={colHeaders}
        attributesOrder={attributesOrder}
        prepareSectionValues={prepareSectionValues}
        handleAddItem={handleAddItem}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
        getItemsFunction={useGetPurchasedProductItems}
        isPreview={isPreview}
        maxHeight={isPreview ? "240" : "210"}
      />}

      <ManualInput
        register={register}
        errors={errors}
        control={control}
        watch={watch}
        setValue={setValue}
        isPreview={isPreview}
      />

      <FileAttachment
        type={"purchased-goods-and-services"}
        uploadHook={useAttachFileToCategory}
        downloadHook={useGetCategoryAttachment}
        deleteHook={useDeleteCategoryAttachment}
        invalidationKey={organizationQuestionnaireKeys.orgQuestionnaireById()}
        currentAttachment={attachment}
        params={{
          questionnaireId: questionnaireId,
          categoryId: categoryId
        }}
        isDisabled={isPreview}
      />

      <Box paddingTop="4">
        <FormTextArea
          label={t("inputs.notes")}
          errors={errors}
          {...register("notes")}
          maxHeight="50"
          disabled={isPreview}
        />
      </Box>

      <DeleteDialog
        isOpen={isDeleteItemDialogOpen}
        onClose={handleDeleteItemDialogClose}
        onDelete={deleteRecord}
        entityName={"Item"}
      />
      {
        isCategoryModalOpen && categoryModalData !== null && (
          <PurchasedAndCapitalGoodsModalWrapper
            tabKey={tabKey}
            modalData={categoryModalData}
            attributesOrder={attributesOrder}
            isOpen={isCategoryModalOpen}
            onClose={handleCategoryModalClose}
            onSubmit={handleCategoryModalSubmit}
            locations={locations}
          />
        )
      }
    </CategoryForm>
  );
}

export default PurchasedGoodsAndServices;